// Updated currencyUtils.js

// Fetch exchange rates from TCMB API through PocketBase custom endpoint
const fetchTCMBRates = async (pb) => {
    try {
        // Fetch rates for main currencies
        // Fetch responses
        const [usdResponse, eurResponse, gbpResponse] = await Promise.all([
            pb.send('/api/custom/tcmb/currency/USD', {
                method: 'GET',
                query: { full: 'true' }
            }),
            pb.send('/api/custom/tcmb/currency/EUR', {
                method: 'GET',
                query: { full: 'true' }
            }),
            pb.send('/api/custom/tcmb/currency/GBP', {
                method: 'GET',
                query: { full: 'true' }
            })
        ]);

        // Convert TCMB date format to ISO format
        const formatTCMBDate = (dateStr) => {
            const [month, day, year] = dateStr.split('/');
            return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
        };

        return {
            date: usdResponse.Date || '',
            turkishDate: usdResponse.Tarih || '',
            bulletinNo: usdResponse.BultenNo,
            rates: {
                usdBuying: usdResponse.ForexAlis,
                usdSelling: usdResponse.ForexSatis,
                eurBuying: eurResponse.ForexAlis,
                eurSelling: eurResponse.ForexSatis,
                gbpBuying: gbpResponse.ForexAlis,
                gbpSelling: gbpResponse.ForexSatis
            }
        };
    } catch (error) {
        console.error('Failed to fetch TCMB rates:', error);
        throw error;
    }
};

// Check for existing active rates and their invoice relationships
const checkExistingRates = async (pb) => {
    try {
        const existingRates = await pb.collection('exchange_rates').getList(1, 1, {
            sort: '-created',
            filter: 'active = true',
            expand: 'teklifler'
        });

        return existingRates.items.length > 0 ? existingRates.items[0] : null;
    } catch (error) {
        console.error('Failed to check existing rates:', error);
        throw error;
    }
};

// Update or create exchange rates based on invoice relationships
export const updateExchangeRates = async (pb) => {
    try {
        const tcmbRates = await fetchTCMBRates(pb);
        const existingRates = await checkExistingRates(pb);

        // Prepare the rates data
        const ratesData = {
            date: tcmbRates.date,                // "12/30/2024"
            turkishDate: tcmbRates.turkishDate,  // "30.12.2024"
            bulletinNo: tcmbRates.bulletinNo,    // "2024/247"
            active: true,
            ...tcmbRates.rates,
            teklifler: []
        };

        console.log('Preparing to save rates:', ratesData);

        // If no existing rates, create new record
        if (!existingRates) {
            return await pb.collection('exchange_rates').create(ratesData);
        }

        // If existing rates have invoice relationships
        if (existingRates.expand?.teklifler?.length > 0) {
            // Deactivate existing record
            await pb.collection('exchange_rates').update(existingRates.id, {
                active: false
            });

            // Create new record
            return await pb.collection('exchange_rates').create(ratesData);
        }

        // If existing rates have no invoice relationships, update current record
        return await pb.collection('exchange_rates').update(existingRates.id, ratesData);
    } catch (error) {
        console.error('Failed to update exchange rates:', error);
        throw error;
    }
};

// Log TCMB response for debugging
const logTCMBResponse = (response) => {
    console.log('TCMB Response:', {
        date: response.Date,
        turkishDate: response.Tarih,
        bulletinNo: response.BultenNo
    });
};

// Fetch current active exchange rates
export const fetchExchangeRates = async (pb) => {
    try {
        // First get the TCMB rates directly
        const tcmbRates = await fetchTCMBRates(pb);
        console.log('TCMB Rates:', tcmbRates);  // Debug log
        
        const result = await pb.collection('exchange_rates').getList(1, 1, {
            sort: '-created',
            filter: 'active = true'
        });

        if (!result.items.length) {
            throw new Error('No active exchange rates found');
        }

        const rates = result.items[0];
        console.log('DB Rates:', rates);  // Debug log
        
        return {
            date: tcmbRates.date,           // Use TCMB date
            turkishDate: tcmbRates.turkishDate, // Use TCMB Turkish date
            bulletinNo: tcmbRates.bulletinNo,
            currencies: {
                USD: {
                    kod: 'USD',
                    birim: 1,
                    isim: 'ABD DOLARI',
                    currencyName: 'US DOLLAR',
                    forexBuying: parseFloat(rates.usdBuying),
                    forexSelling: parseFloat(rates.usdSelling)
                },
                EUR: {
                    kod: 'EUR',
                    birim: 1,
                    isim: 'EURO',
                    currencyName: 'EURO',
                    forexBuying: parseFloat(rates.eurBuying),
                    forexSelling: parseFloat(rates.eurSelling)
                },
                GBP: {
                    kod: 'GBP',
                    birim: 1,
                    isim: 'İNGİLİZ STERLİNİ',
                    currencyName: 'POUND STERLING',
                    forexBuying: parseFloat(rates.gbpBuying),
                    forexSelling: parseFloat(rates.gbpSelling)
                }
            }
        };
    } catch (error) {
        console.error('Failed to fetch exchange rates:', error);
        throw error;
    }
};

// Currency conversion function with TRY support
export const convertCurrency = (amount, fromCurrency, toCurrency, rates) => {
    if (!amount || isNaN(amount)) return 0;
    if (!rates?.currencies) {
        console.warn('Exchange rates not available for conversion');
        return amount;
    }

    // Normalize currency codes
    fromCurrency = fromCurrency === 'TL' ? 'TRY' : fromCurrency;
    toCurrency = toCurrency === 'TL' ? 'TRY' : toCurrency;
    
    if (fromCurrency === toCurrency) return amount;

    try {
        if (fromCurrency === 'TRY') {
            return amount / rates.currencies[toCurrency].forexSelling;
        }
        
        if (toCurrency === 'TRY') {
            return amount * rates.currencies[fromCurrency].forexSelling;
        }
        
        const amountInTRY = amount * rates.currencies[fromCurrency].forexSelling;
        return amountInTRY / rates.currencies[toCurrency].forexSelling;
    } catch (error) {
        console.error('Currency conversion failed:', error);
        return amount;
    }
};

// Format currency with better error handling and TRY support
export const formatCurrency = (amount, currency = 'TRY') => {
    if (!amount || isNaN(amount)) return '0,00';
    
    try {
        // Handle special case for Turkish Lira
        if (currency === 'TL') {
            currency = 'TRY';
        }

        const options = {
            style: 'currency',
            currency: currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        };

        // Use tr-TR locale for proper Turkish formatting
        return new Intl.NumberFormat('tr-TR', options).format(amount);
    } catch (error) {
        console.error('Currency formatting failed:', error);
        // Fallback formatting for Turkish Lira
        if (currency === 'TRY') {
            return `₺${amount.toFixed(2).replace('.', ',')}`;
        }
        // Fallback for other currencies
        return `${amount.toFixed(2)} ${currency}`;
    }
};

// Calculate totals with TRY support
export const calculateTotals = (items, exchangeRates, baseCurrency = 'TRY') => {
    if (!items?.length || !exchangeRates?.currencies) {
        return { TRY: 0 };
    }
    
    try {
        const totals = {
            TRY: 0,
            USD: 0,
            EUR: 0,
            GBP: 0
        };
        
        items.forEach(item => {
            const amount = parseFloat(item.total_amount || item.total || 0);
            // Normalize currency code
            const currency = (item.currency === 'TL' ? 'TRY' : item.currency) || baseCurrency;
            
            Object.keys(totals).forEach(targetCurrency => {
                const convertedAmount = convertCurrency(
                    amount,
                    currency,
                    targetCurrency,
                    exchangeRates
                );
                totals[targetCurrency] += convertedAmount;
            });
        });
        
        return totals;
    } catch (error) {
        console.error('Total calculation failed:', error);
        return { TRY: 0 };
    }
};

// Update invoice with exchange rate
export const updateInvoiceWithExchangeRate = async (pb, invoiceData) => {
    try {
        // Normalize currency
        invoiceData.currency = invoiceData.currency === 'TL' ? 'TRY' : invoiceData.currency;
        
        // First update exchange rates
        await updateExchangeRates(pb);
        
        // Then fetch current rates
        const rates = await fetchExchangeRates(pb);
        
        const updatedInvoiceData = {
            ...invoiceData,
            exchange_rate_date: rates.date,
            exchange_rate_bulletin_no: rates.bulletinNo,
            currency: invoiceData.currency || 'TRY'
        };

        if (updatedInvoiceData.currency !== 'TRY') {
            updatedInvoiceData.amount_try = convertCurrency(
                invoiceData.total_amount,
                updatedInvoiceData.currency,
                'TRY',
                rates
            );
        } else {
            updatedInvoiceData.amount_try = invoiceData.total_amount;
        }

        return updatedInvoiceData;
    } catch (error) {
        console.error('Failed to update invoice with exchange rate:', error);
        throw error;
    }
};

// Update quote with exchange rate
export const updateQuoteWithExchangeRate = async (pb, quoteData) => {
    try {
        // First update exchange rates
        await updateExchangeRates(pb);
        
        // Then fetch current rates
        const rates = await fetchExchangeRates(pb);
        
        const updatedQuoteData = {
            ...quoteData,
            exchange_rate_date: rates.date,
            exchange_rate_bulletin_no: rates.bulletinNo
        };

        if (quoteData.currency !== 'TRY') {
            updatedQuoteData.amount_try = convertCurrency(
                quoteData.total_amount,
                quoteData.currency,
                'TRY',
                rates
            );
        }

        return updatedQuoteData;
    } catch (error) {
        console.error('Failed to update quote with exchange rate:', error);
        throw error;
    }
};